import { useNavigate } from 'react-router-dom';
import SVG from './SVG';
import { TableType } from '@/config';
import { useAppContext } from '@/use-context/Context';
import { translateLanguage } from '@/helper/i18n';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer } from 'easytimer.js';
import Lucide from '@/components/Base/Lucide';
import { renderTime } from '@/helper';
import { getShop } from '@/utils/auth';

interface ListTableProps {
    table: TableType;
}

export default function ListTable({ table }: ListTableProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setLoading } = useAppContext();
    const shop = getShop();

    const [color, setColor] = useState<string>("");

    const handleTabel = () => {
        setLoading(true);
        navigate(`/waiter-order/${table._id}`);
        localStorage.setItem("tableName", table.name);
        localStorage.setItem("tableId", table._id);
        localStorage.setItem("categoryId", "");

        setTimeout(() => setLoading(false), 500);
    };

    useEffect(() => {
        const checkColors = ({
            chef,
            newOrder,
            support,
        }: {
            chef: boolean;
            newOrder: boolean;
            support: boolean;
        }) => {
            if (chef) setColor("chef");
            else if (newOrder) setColor("newOrder");
            else if (support) setColor("support");
            else setColor("");
        };

        checkColors({
            chef: table.colorStatus.chef,
            newOrder: table.colorStatus.newOrder,
            support: table.colorStatus.support,
        });
    }, [table.colorStatus]);

    const colorClasses: Record<string, string> = {
        chef: "bg-[#52DF77]/50 animate-pulseCustom",
        newOrder: "bg-[#527FDF]/50 animate-pulseCustom",
        support: "bg-[#6352A2]/50 animate-pulse",
    };

    const statusIndicators = [
        { key: "newOrder", color: "blue", condition: color === "chef" && table.colorStatus.newOrder },
        { key: "supportChef", color: "purple", condition: color === "chef" && table.colorStatus.support },
        { key: "supportNewOrder", color: "purple", condition: color === "newOrder" && table.colorStatus.support },
    ];

    const statuses = [
        table.summary?.status.pending && {
            key: "pending",
            label: t("pending"),
            value: table.summary.status.pending,
            color: "text-yellow-600",
        },
        table.summary?.status.accepted && {
            key: "accepted",
            label: t("accept"),
            value: table.summary.status.accepted,
            color: "text-blue-600",
        },
        table.summary?.status.cooking && {
            key: "cooking",
            label: t("cooking"),
            value: table.summary.status.cooking,
            color: "text-purple-600",
        },
        table.summary?.status.ready && {
            key: "ready",
            label: t("ready"),
            value: table.summary.status.ready,
            color: "text-green-600",
        },
        table.summary?.status.done && {
            key: "done",
            label: t("done"),
            value: table.summary.status.done,
            color: "text-[#64748B]",
        },
        table.summary?.status.returned && {
            key: "returned",
            label: t("returned"),
            value: table.summary.status.returned,
            color: "text-[#F97316]",
        },
    ].filter(Boolean);

    const isOdd = statuses.length % 2 === 1;

    const timerRef = useRef<Timer | null>(null);
    const [timeText, setTimeText] = useState<string>("00:00:00"); // Displayed timer

    const getTrackingTime = (statusTracking: string) => {
        return { time: statusTracking };
    };

    const tracking = table.summary && getTrackingTime(table.summary.longestTime);

    useEffect(() => {
        if (!tracking?.time) return;

        const timer = new Timer();

        const startTime = new Date(tracking.time).getTime();
        const now = Date.now();
        const elapsedSeconds = Math.max(0, Math.floor((now - startTime) / 1000));

        timer.start({
            startValues: { seconds: elapsedSeconds }
        });

        setTimeText(timer.getTimeValues().toString());

        timer.addEventListener('secondsUpdated', () => {
            setTimeText(timer.getTimeValues().toString());
        });

        timerRef.current = timer;

        return () => {
            timer.stop();
        };
    }, [tracking?.time]);

    if (!shop) {
        return null
    }

    return (
        <div className="transition duration-300 ease-in-out hover:-translate-y-1 hover:scale-105 cursor-pointer">
            <div
                className={`${!color && (table.colorStatus.available ? "bg-[#D7D7D7]" : "bg-[#F8E3E3]")}
                rounded-lg p-4 ${color ? colorClasses[color] || "" : ""} relative`}
                onClick={handleTabel}
            >
                <div className="flex justify-between items-start">
                    <div className="relative h-[54px] w-[80px]">
                        <div className="absolute bottom-5">
                            <div className="relative flex size-10">
                                <SVG
                                    fill={table.colorStatus.available ? "#767676" : "#DF5252"}
                                    className="relative inline-flex size-20"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                        <div className="flex gap-x-2 h-3">
                            {statusIndicators.map(({ key, color, condition }) =>
                                condition && (
                                    <span key={key} className="relative flex size-3">
                                        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full bg-${color}-400 opacity-75`} />
                                        <span className={`relative inline-flex size-3 rounded-full bg-${color}-500`} />
                                    </span>
                                )
                            )}
                        </div>

                        <div className="absolute top-8 grid grid-cols-2 gap-x-1 items-end text-right">
                            {statuses.map((item: any, index) => (
                                <div
                                    key={item.key}
                                    className={`text-[11px] font-bold ${isOdd && index === statuses.length - 1 ? "col-start-2" : ""
                                        }`}
                                >
                                    <p className={item.color}>
                                        {item.label} X {item.value}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mt-5 relative">
                    <h3 className="text-base font-medium pl-2">
                        {translateLanguage(table.name, table.nameKh ?? table.name)}
                    </h3>

                    {
                        table.summary && table.summary.longestTime &&
                        <div className={` ${renderTime(timeText, shop.performanceConfig.maxServiceTime)}`}>
                            <div className="flex gap-1 items-center text-[12px] font-semibold pl-2">
                                <Lucide icon="AlarmClock" className="w-4 pb-0.5 stroke-[2]" />
                                {timeText}
                            </div>
                        </div>
                    }

                    {/* {table.orderCount > 1 && (
                        <span className="text-danger bg-white rounded-lg px-2.5 py-1 absolute top-0 right-0">
                            {table.orderCount}
                        </span>
                    )} */}
                </div>
            </div>
        </div>
    );
}
